import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { select, Store } from '@ngrx/store';
import { FileBoxComponent } from '../components/file-box/file-box.component';
import { ProcessingFileCardComponent } from '../components/processing-file-card/processing-file-card.component';
import { ConvertedFile } from '../../models/converted-file';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import {
  selectConvertedFiles,
  selectGerberFiles,
  selectIsFileBoxBlocked,
  selectIsGerberLocked,
  selectIsLectraLocked,
  selectLectraFiles,
  selectUploadedFiles,
} from '../../state/home.selectors';
import {
  clearGerberFileQueue,
  clearLectraFileQueue,
  loadConvertedFiles,
  loadGerberFiles,
  loadLectraFiles,
  reorderGerberFiles,
  reorderLectraFiles,
  requestClearConvertedFiles,
  resetAllGerberFiles,
  resetAllLectraFiles,
} from '../../state/home.actions';
import {
  combineLatest,
  firstValueFrom,
  lastValueFrom,
  map,
  Observable,
  take,
} from 'rxjs';
import { ConvertedFileCardComponent } from '../components/converted-file-card/converted-file-card.component';
import { FilesService } from '../../data/services/files.service';
import JSZip from 'jszip';
import { ToastrService } from 'ngx-toastr';
import { saveAs } from 'file-saver';
import { ConfirmationModalComponent } from '../components/confirmation-modal/confirmation-modal.component';
import { AuthService } from '../../../auth/data/services/auth.service';
import { environment } from '../../../../../environments/environment';
import { TranslatePipe } from '@ngx-translate/core';
import { UploadedFile } from '../../models/uploaded-file';
import { JobStatus } from '../../models/job-status';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    FileBoxComponent,
    ProcessingFileCardComponent,
    CommonModule,
    DragDropModule,
    HttpClientModule,
    ConvertedFileCardComponent,
    ConfirmationModalComponent,
    TranslatePipe,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit {
  http = inject(HttpClient);
  fileService = inject(FilesService);
  authService = inject(AuthService);
  baseUrl = environment.apiUrl;
  isFileBoxBlocked$: Observable<boolean>;
  isGerberLocked$: Observable<boolean>;
  isLectraLocked$: Observable<boolean>;
  isAnyQueueLocked$: Observable<boolean>;
  selectedFiles: ConvertedFile[] = [];
  combinedFiles$: Observable<(UploadedFile | ConvertedFile)[]>;
  protected showCheckboxes: boolean = true;
  protected gerberFilesCount$: Observable<number> = new Observable<number>();
  protected lectraFilesCount$: Observable<number> = new Observable<number>();
  protected convertedFilesCount$: Observable<number> = new Observable<number>();
  protected totalQueueCount$: Observable<number> = new Observable<number>();
  protected showConfirmation = false;
  protected confirmationModalMessage = '';
  protected showConvertedLimitModal = false;
  protected queueToProcess: 'gerber' | 'lectra' | 'both' | null = null;
  private toastr = inject(ToastrService);
  private store = inject(Store);
  protected uploadedFiles$ = this.store.pipe(
    select(selectUploadedFiles),
    map((files) => files.filter((file) => file.fileName !== undefined)),
  );
  protected gerberFiles$ = this.store.pipe(
    select(selectGerberFiles),
    map((files) => files.filter((file) => file.fileName !== undefined)),
  );
  protected lectraFiles$ = this.store.pipe(
    select(selectLectraFiles),
    map((files) => files.filter((file) => file.fileName !== undefined)),
  );
  protected convertedFiles$ = this.store.pipe(select(selectConvertedFiles));
  protected allQueueFiles$: Observable<UploadedFile[]>;
  private readonly MAX_FILE_COUNT = 50;

  constructor() {
    this.isFileBoxBlocked$ = this.store.select(selectIsFileBoxBlocked);
    this.isGerberLocked$ = this.store.select(selectIsGerberLocked);
    this.isLectraLocked$ = this.store.select(selectIsLectraLocked);
    this.isAnyQueueLocked$ = combineLatest([
      this.isGerberLocked$,
      this.isLectraLocked$,
    ]).pipe(
      map(([gerberLocked, lectraLocked]) => gerberLocked || lectraLocked),
    );
    this.gerberFilesCount$ = this.gerberFiles$.pipe(
      map((files) => files.length),
    );
    this.lectraFilesCount$ = this.lectraFiles$.pipe(
      map((files) => files.length),
    );
    this.convertedFilesCount$ = this.convertedFiles$.pipe(
      map((files) => files.length),
    );
    this.combinedFiles$ = combineLatest([
      this.uploadedFiles$,
      this.convertedFiles$,
    ]).pipe(
      map(([uploadedFiles, convertedFiles]) => [
        ...uploadedFiles,
        ...convertedFiles,
      ]),
    );
    this.allQueueFiles$ = combineLatest([
      this.gerberFiles$,
      this.lectraFiles$,
    ]).pipe(
      map(([gerberFiles, lectraFiles]) => {
        const allFiles = [...gerberFiles, ...lectraFiles];
        return allFiles.sort((a, b) => {
          const statusPriority = (status: JobStatus) => {
            if (status === JobStatus.Processing) return 0;
            if (status === JobStatus.Wait) return 1;
            if (status === JobStatus.Error) return 2;
            return 3;
          };
          return statusPriority(a.status) - statusPriority(b.status);
        });
      }),
    );
    this.isFileBoxBlocked$ = this.store.select(selectIsFileBoxBlocked);
    this.totalQueueCount$ = combineLatest([
      this.gerberFilesCount$,
      this.lectraFilesCount$,
    ]).pipe(map(([g, l]) => g + l));
  }

  ngOnInit(): void {
    this.store.dispatch(resetAllGerberFiles());
    this.store.dispatch(resetAllLectraFiles());
    this.store.dispatch(loadGerberFiles());
    this.store.dispatch(loadLectraFiles());
    this.store.dispatch(loadConvertedFiles());
    this.fileService.startMonitoring();
  }

  protected toggleCheckboxes() {
    this.showCheckboxes = !this.showCheckboxes;
  }

  protected selectAllFiles(convertedFiles: ConvertedFile[]) {
    this.selectedFiles = [...convertedFiles];
  }

  protected clearSelectedFiles() {
    this.selectedFiles = [];
  }

  protected toggleFileSelection(file: any) {
    const index = this.selectedFiles.findIndex((f) => f.fileId === file.fileId);
    if (index > -1) {
      this.selectedFiles.splice(index, 1);
    } else {
      this.selectedFiles.push(file);
    }
  }

  protected async downloadConvertedFiles() {
    const filesToDownload =
      this.selectedFiles.length > 0
        ? this.selectedFiles
        : await lastValueFrom(this.convertedFiles$.pipe(take(1)));

    if (!filesToDownload || filesToDownload.length === 0) {
      this.toastr.warning('No converted files to download.');
      return;
    }

    const token = await firstValueFrom(this.authService.getToken());
    if (!token) {
      this.toastr.error('User not authenticated.');
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };
    const zip = new JSZip();
    const filePromises: Promise<void>[] = [];

    for (const file of filesToDownload) {
      const url = `${this.baseUrl}api/Blob/download`;

      filePromises.push(
        lastValueFrom(
          this.http.get(url, {
            params: { fileId: file.fileId },
            headers,
            observe: 'response',
            responseType: 'blob',
          }),
        )
          .then((response) => {
            const blob = response.body;
            const contentDisposition = response.headers.get(
              'Content-Disposition',
            );
            const filenameMatch = /filename="?([^"]+)"?/.exec(
              contentDisposition || '',
            );
            const filename = filenameMatch?.[1] || file.convertedFileName;

            if (!blob) throw new Error(`Empty blob for ${filename}`);
            zip.file(filename, blob);
          })
          .catch((err) => {
            console.error(`Error downloading file ${file.fileName}`, err);
            this.toastr.error(`Failed to download: ${file.fileName}`);
          }),
      );
    }

    await Promise.all(filePromises);

    if (Object.keys(zip.files).length === 0) {
      this.toastr.error('No files could be downloaded.');
      return;
    }

    zip
      .generateAsync({ type: 'blob' })
      .then((content) => {
        saveAs(content, 'converted-files.zip');
      })
      .catch((error) => {
        console.error('Error generating ZIP file:', error);
        this.toastr.error('Failed to generate ZIP file.');
      });
  }

  protected cancelGerberQueue() {
    return this.fileService.cancelGerberFileProcessing();
  }

  protected cancelBothQueues() {
    this.fileService.cancelAllProcessing();
  }

  protected cancelLectraQueue() {
    return this.fileService.cancelLectraFileProcessing();
  }

  protected callClearConvertedFilesConfirmation() {
    this.showConfirmation = true;
    this.confirmationModalMessage = 'Delete all converted files?';
  }

  protected clearConvertedFiles() {
    firstValueFrom(this.convertedFiles$).then((files) => {
      if (files.length > 0) {
        this.showConfirmation = false;
        this.store.dispatch(requestClearConvertedFiles());
      }
    });
  }

  protected clearGerberQueue() {
    this.store.dispatch(clearGerberFileQueue());
  }

  protected clearLectraQueue() {
    this.store.dispatch(clearLectraFileQueue());
  }

  protected clearBothQueues() {
    this.store.dispatch(clearGerberFileQueue());
    this.store.dispatch(clearLectraFileQueue());
  }

  protected sendGerberQueue() {
    combineLatest([
      this.store.select(selectConvertedFiles).pipe(take(1)),
      this.store.select(selectGerberFiles).pipe(take(1)),
    ]).subscribe(([convertedFiles, gerberFiles]) => {
      const totalFiles = convertedFiles.length + gerberFiles.length;

      if (totalFiles > this.MAX_FILE_COUNT) {
        this.queueToProcess = 'gerber';
        this.showConvertedLimitModal = true;
      } else {
        this.processGerberQueue();
      }
    });
  }

  protected sendLectraQueue() {
    combineLatest([
      this.store.select(selectConvertedFiles).pipe(take(1)),
      this.store.select(selectLectraFiles).pipe(take(1)),
    ]).subscribe(([convertedFiles, lectraFiles]) => {
      const totalFiles = convertedFiles.length + lectraFiles.length;

      if (totalFiles > this.MAX_FILE_COUNT) {
        this.queueToProcess = 'lectra';
        this.showConvertedLimitModal = true;
      } else {
        this.processLectraQueue();
      }
    });
  }

  protected sendBothQueues() {
    combineLatest([
      this.store.select(selectConvertedFiles).pipe(take(1)),
      this.store.select(selectGerberFiles).pipe(take(1)),
      this.store.select(selectLectraFiles).pipe(take(1)),
      this.isGerberLocked$.pipe(take(1)),
      this.isLectraLocked$.pipe(take(1)),
    ]).subscribe(
      ([
        convertedFiles,
        gerberFiles,
        lectraFiles,
        isGerberLocked,
        isLectraLocked,
      ]) => {
        if (isGerberLocked && isLectraLocked) {
          return;
        }

        let totalFiles = convertedFiles.length;

        if (!isGerberLocked && gerberFiles.length > 0) {
          totalFiles += gerberFiles.length;
        }

        if (!isLectraLocked && lectraFiles.length > 0) {
          totalFiles += lectraFiles.length;
        }

        if (totalFiles > this.MAX_FILE_COUNT) {
          this.queueToProcess = 'both';
          this.showConvertedLimitModal = true;
        } else {
          this.processBothQueues();
        }
      },
    );
  }

  protected confirmProcessQueue() {
    this.showConvertedLimitModal = false;

    switch (this.queueToProcess) {
      case 'gerber':
        this.processGerberQueue();
        break;
      case 'lectra':
        this.processLectraQueue();
        break;
      case 'both':
        this.processBothQueues();
        break;
    }

    this.queueToProcess = null;
  }

  protected processBothQueues() {
    this.showConvertedLimitModal = false;
    combineLatest([
      this.isGerberLocked$.pipe(take(1)),
      this.isLectraLocked$.pipe(take(1)),
    ]).subscribe(([isGerberLocked, isLectraLocked]) => {
      if (!isGerberLocked) {
        this.fileService.processGerberFileQueue(this.gerberFiles$);
      }
      if (!isLectraLocked) {
        this.fileService.processLectraFileQueue(this.lectraFiles$);
      }
    });
  }

  protected processGerberQueue() {
    this.showConvertedLimitModal = false;
    this.fileService.processGerberFileQueue(this.gerberFiles$);
  }

  protected processLectraQueue() {
    this.showConvertedLimitModal = false;
    this.fileService.processLectraFileQueue(this.lectraFiles$);
  }

  protected getFileType(file: UploadedFile): 'gerber' | 'lectra' {
    return file.conversionMode === 'GerberToLectra' ? 'gerber' : 'lectra';
  }

  protected drop(event: CdkDragDrop<UploadedFile[]>, allFiles: UploadedFile[]) {
    if (event.previousIndex !== event.currentIndex) {
      const movedFile = allFiles[event.previousIndex];
      const fileType = this.getFileType(movedFile);

      combineLatest([this.gerberFiles$, this.lectraFiles$])
        .pipe(take(1))
        .subscribe(([gerberFiles, lectraFiles]) => {
          if (fileType === 'gerber') {
            const gerberIndex = gerberFiles.findIndex(
              (f) => f.fileId === movedFile.fileId,
            );
            const targetFile = allFiles[event.currentIndex];
            const targetType = this.getFileType(targetFile);

            let newIndex = event.currentIndex;
            if (targetType === 'lectra') {
              newIndex = gerberFiles.length - 1;
            } else {
              const filesBeforeTarget = allFiles
                .slice(0, event.currentIndex)
                .filter((f) => this.getFileType(f) === 'gerber');
              newIndex = filesBeforeTarget.length;
            }

            this.store.dispatch(
              reorderGerberFiles({
                previousIndex: gerberIndex,
                currentIndex: newIndex,
              }),
            );
          } else {
            const lectraIndex = lectraFiles.findIndex(
              (f) => f.fileId === movedFile.fileId,
            );
            const targetFile = allFiles[event.currentIndex];
            const targetType = this.getFileType(targetFile);

            let newIndex = event.currentIndex;
            if (targetType === 'gerber') {
              newIndex = 0;
            } else {
              const filesBeforeTarget = allFiles
                .slice(0, event.currentIndex)
                .filter((f) => this.getFileType(f) === 'lectra');
              newIndex = filesBeforeTarget.length;
            }

            this.store.dispatch(
              reorderLectraFiles({
                previousIndex: lectraIndex,
                currentIndex: newIndex,
              }),
            );
          }
        });
    }
  }
}
